import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { label: "首页", to: "/" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setMobileCategoryOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="色色视频首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-xl" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-2xl leading-none tracking-wide text-primary-600">
            色色视频
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`
            }
          >
            首页
          </NavLink>

          <div className="group relative">
            <button
              type="button"
              className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              分类
              <i className="ri-arrow-down-s-line text-base" aria-hidden="true"></i>
            </button>
            <div className="invisible absolute left-0 top-full w-[440px] translate-y-1 rounded-xl border border-background-200 bg-background-50 p-3 opacity-0 shadow-none transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className={`${cat.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {navItems.slice(1).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden w-full max-w-sm items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 md:flex"
          role="search"
        >
          <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索视频、分类、标签…"
            aria-label="站内搜索"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 md:hidden"
          aria-label="打开菜单"
        >
          <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-2-line text-2xl"} aria-hidden="true"></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 md:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-4 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索视频关键词"
              aria-label="站内搜索"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50"
            >
              搜索
            </button>
          </form>

          <nav className="flex flex-col gap-1">
            <Link to="/" className="rounded-lg px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            <button
              type="button"
              onClick={() => setMobileCategoryOpen((prev) => !prev)}
              className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
            >
              分类
              <i className={mobileCategoryOpen ? "ri-arrow-up-s-line text-lg" : "ri-arrow-down-s-line text-lg"} aria-hidden="true"></i>
            </button>
            {mobileCategoryOpen && (
              <div className="grid grid-cols-2 gap-1 pb-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50"
                  >
                    <i className={`${cat.icon} text-base`} aria-hidden="true"></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
            <Link to="/search" className="rounded-lg px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100">
              搜索
            </Link>
            <Link to="/about" className="rounded-lg px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100">
              关于
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}